import { useEffect, useRef, RefObject } from 'react';

export interface SwipeGestureConfig {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  onLongPress?: () => void;
  onDoubleTap?: () => void;
  minSwipeDistance?: number;
  maxSwipeTime?: number;
  longPressDelay?: number;
  doubleTapDelay?: number;
}

interface TouchData {
  startX: number;
  startY: number;
  startTime: number;
  endX: number;
  endY: number;
  endTime: number;
}

/**
 * useSwipeGestures Hook
 * Détecte et gère les gestes tactiles sur mobile
 *
 * Gestes supportés :
 * - Swipe Left/Right/Up/Down
 * - Long Press
 * - Double Tap
 *
 * @example
 * useSwipeGestures(elementRef, {
 *   onSwipeLeft: () => console.log('Swiped left'),
 *   onSwipeRight: () => console.log('Swiped right'),
 *   onLongPress: () => console.log('Long press'),
 * });
 */
export function useSwipeGestures<T extends HTMLElement>(
  // V2.G12.6: accept `T | null` to match React 18's useRef return type.
  ref: RefObject<T | null>,
  config: SwipeGestureConfig
) {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    onLongPress,
    onDoubleTap,
    minSwipeDistance = 50,
    maxSwipeTime = 300,
    longPressDelay = 500,
    doubleTapDelay = 300
  } = config;

  const touchDataRef = useRef<TouchData | null>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastTapTimeRef = useRef<number>(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchDataRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        endX: touch.clientX,
        endY: touch.clientY,
        endTime: Date.now()
      };

      // Long press detection
      if (onLongPress) {
        longPressTimerRef.current = setTimeout(() => {
          onLongPress();
          touchDataRef.current = null; // Cancel swipe after long press
        }, longPressDelay);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchDataRef.current) return;

      // Cancel long press if finger moves
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      const touch = e.touches[0];
      touchDataRef.current.endX = touch.clientX;
      touchDataRef.current.endY = touch.clientY;
      touchDataRef.current.endTime = Date.now();
    };

    const handleTouchEnd = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      if (!touchDataRef.current) return;

      const { startX, startY, startTime, endX, endY, endTime } = touchDataRef.current;
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const deltaTime = endTime - startTime;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // Double tap detection
      const now = Date.now();
      if (absDeltaX < 10 && absDeltaY < 10 && deltaTime < 200) {
        if (onDoubleTap && now - lastTapTimeRef.current < doubleTapDelay) {
          onDoubleTap();
          lastTapTimeRef.current = 0; // Reset to avoid triple tap
        } else {
          lastTapTimeRef.current = now;
        }
      }

      // Swipe detection
      if (deltaTime < maxSwipeTime) {
        // Horizontal swipe
        if (absDeltaX > absDeltaY && absDeltaX > minSwipeDistance) {
          if (deltaX > 0 && onSwipeRight) {
            onSwipeRight();
          } else if (deltaX < 0 && onSwipeLeft) {
            onSwipeLeft();
          }
        }
        // Vertical swipe
        else if (absDeltaY > absDeltaX && absDeltaY > minSwipeDistance) {
          if (deltaY > 0 && onSwipeDown) {
            onSwipeDown();
          } else if (deltaY < 0 && onSwipeUp) {
            onSwipeUp();
          }
        }
      }

      touchDataRef.current = null;
    };

    const handleTouchCancel = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      touchDataRef.current = null;
    };

    // Add event listeners
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd);
    element.addEventListener('touchcancel', handleTouchCancel);

    // Cleanup
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchCancel);

      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, [
    ref,
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    onLongPress,
    onDoubleTap,
    minSwipeDistance,
    maxSwipeTime,
    longPressDelay,
    doubleTapDelay
  ]);
}

/**
 * Helper pour afficher un indicateur visuel de swipe
 */
export function createSwipeIndicator(direction: 'left' | 'right' | 'up' | 'down'): void {
  const indicator = document.createElement('div');
  indicator.className = 'swipe-indicator';
  indicator.style.cssText = `
    position: fixed;
    ${direction === 'left' ? 'right: 20px;' : ''}
    ${direction === 'right' ? 'left: 20px;' : ''}
    ${direction === 'up' ? 'bottom: 20px;' : ''}
    ${direction === 'down' ? 'top: 20px;' : ''}
    ${direction === 'left' || direction === 'right' ? 'top: 50%; transform: translateY(-50%);' : ''}
    ${direction === 'up' || direction === 'down' ? 'left: 50%; transform: translateX(-50%);' : ''}
    width: 50px;
    height: 50px;
    background: rgba(99, 102, 241, 0.8);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    color: white;
    z-index: 10000;
    pointer-events: none;
    animation: swipeIndicatorFade 0.5s ease-out forwards;
  `;

  const arrows: { [key: string]: string } = {
    left: '←',
    right: '→',
    up: '↑',
    down: '↓'
  };

  indicator.textContent = arrows[direction];
  document.body.appendChild(indicator);

  // Auto remove after animation
  setTimeout(() => {
    indicator.remove();
  }, 500);
}

// Add CSS animation if not exists
if (typeof document !== 'undefined' && !document.getElementById('swipe-indicator-styles')) {
  const style = document.createElement('style');
  style.id = 'swipe-indicator-styles';
  style.textContent = `
    @keyframes swipeIndicatorFade {
      0% {
        opacity: 0;
        transform: scale(0.5) ${window.matchMedia ? (window.matchMedia('(orientation: portrait)').matches ? 'translateY(-50%)' : 'translateX(-50%)') : ''};
      }
      50% {
        opacity: 1;
        transform: scale(1) ${window.matchMedia ? (window.matchMedia('(orientation: portrait)').matches ? 'translateY(-50%)' : 'translateX(-50%)') : ''};
      }
      100% {
        opacity: 0;
        transform: scale(0.5) ${window.matchMedia ? (window.matchMedia('(orientation: portrait)').matches ? 'translateY(-50%)' : 'translateX(-50%)') : ''};
      }
    }
  `;
  document.head.appendChild(style);
}
